import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * FormField — wraps a control with its label, helper text, and error.
 *
 *   <FormField label="Email" htmlFor="email" helper="We'll send invoices here.">
 *     <Input id="email" type="email" />
 *   </FormField>
 *
 *   <FormField label="Password" htmlFor="pw" error={errors.password}>
 *     <Input id="pw" type="password" />
 *   </FormField>
 *
 * Pass `required` to render the asterisk. Error replaces helper text when set.
 *
 * The helper and error text are WIRED to the control, not merely placed near
 * it. Before 2026-08-11 they were rendered as loose <p> siblings: a sighted
 * user saw "Enter the domain without www" or "That address is already in use"
 * sitting under the box, and a screen-reader user heard the label and nothing
 * else — the one message that explains how to fix the error was the one
 * message they could not reach. The wiring is `aria-describedby` on the child
 * plus `aria-invalid` when an error is present.
 *
 * Likewise the red asterisk carried "required" in colour and glyph alone. It is
 * now decorative (`aria-hidden`) and the fact is carried by `aria-required` on
 * the control.
 *
 * Both work by cloning the single element child, so no caller has to change.
 * A caller that passes several children, or a child that already sets these
 * attributes, is left alone — an explicit value always wins.
 */

interface FormFieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  helper?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

function FormField({
  label,
  htmlFor,
  helper,
  error,
  required,
  className,
  children,
}: FormFieldProps) {
  const reactId = React.useId();
  const messageId = error ? `${reactId}-error` : helper ? `${reactId}-helper` : undefined;

  const child = React.Children.only(children);
  const described = React.isValidElement(child)
    ? React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        // Never clobber what the caller set explicitly.
        'aria-describedby':
          (child.props as Record<string, unknown>)['aria-describedby'] ??
          messageId,
        'aria-invalid':
          (child.props as Record<string, unknown>)['aria-invalid'] ??
          (error ? true : undefined),
        'aria-required':
          (child.props as Record<string, unknown>)['aria-required'] ??
          (required ? true : undefined),
      })
    : child;

  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="block text-xs font-medium text-foreground"
      >
        {label}
        {required && (
          <span className="ml-0.5 text-danger-dark" aria-hidden>
            *
          </span>
        )}
      </label>
      {described}
      {error ? (
        <p id={messageId} className="mt-1 text-xs text-danger-dark" role="alert">
          {error}
        </p>
      ) : helper ? (
        <p id={messageId} className="mt-1 text-xs text-muted-foreground">
          {helper}
        </p>
      ) : null}
    </div>
  );
}

export { FormField };
export type { FormFieldProps };
